import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, ActivityIndicator, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AiBotIcon } from './icons/AppIcon';
import { apiService } from '../services/api';
import type { AiQuickSuggestions, AiCommerceChoice, AiCommerceLine, AiMedia } from '../services/ai.types';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { usePermissions } from '../hooks/usePermissions';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { colors, spacing } from '../theme';

export function AiConversationAssistant({ conversationId, contextVersion, contextReady = true, disabled, sentVersion = 0, onUseReply, onUseChoices, onSendNow, onSendMedia, onSelectTemplate, onAction }: {
  conversationId: string; contextVersion?: string; contextReady?: boolean; disabled?: boolean;
  /** Changes whenever the agent sends a message: the current suggestion is then used up. */
  sentVersion?: number;
  onUseReply: (content: string) => void;
  onUseChoices: (content: string, choices: AiCommerceChoice[]) => void;
  /** Sends the recommended message (with its buttons) straight away. */
  onSendNow?: (content: string, choices: AiCommerceChoice[]) => void;
  /** Sends a product card, catalogue or product photos the assistant suggested. */
  onSendMedia?: (media: AiMedia) => void;
  onSelectTemplate: (templateId: string) => void;
  onAction: (action: 'order' | 'quotation', lines: AiCommerceLine[]) => void;
}) {
  const styles = useThemedStyles(getStyles);
  const { t, isRTL, locale } = useLocale();
  const businessId = useAppStore(s => s.currentBusiness?._id);
  const role = useAppStore(s => s.businessRole);
  const { hasPermissionAtLeast, refreshPermissions } = usePermissions();
  const permitted = role === 'owner' || hasPermissionAtLeast('ai.useAssistant', 'edit');
  const [ready, setReady] = useState(false);
  const [entitled, setEntitled] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);
  const [setupError, setSetupError] = useState('');
  const [setupReload, setSetupReload] = useState(0);
  const [reload, setReload] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [suggestions, setSuggestions] = useState<AiQuickSuggestions | null>(null);
  const [busy, setBusy] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState('');
  const requestVersion = useRef(0);
  const refreshNext = useRef(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [notice, setNotice] = useState('');
  // After a reply is sent, the suggestion for that customer message is used up: the panel
  // shows the saved Quick Replies until the customer writes again.
  const usedFor = useRef<string | null>(null);
  const [showQuick, setShowQuick] = useState(false);
  const [quickSets, setQuickSets] = useState<Array<{ _id: string; name: string }> | null>(null);
  const [sendingSet, setSendingSet] = useState<string | null>(null);
  const scope = `${businessId}:${conversationId}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  useEffect(() => { if (businessId) void refreshPermissions(); }, [businessId]);
  useEffect(() => { setCollapsed(false); setManageOpen(false); setConfirmClear(false); setNotice(''); }, [scope]);

  // For stuck chats: forget what the assistant remembered, optionally rebuilding right away.
  const resetMemory = async (rebuild: boolean) => {
    const target = scope;
    setResetting(true); setError(''); setNotice('');
    try {
      const response = await apiService.resetAiMemory(conversationId, rebuild);
      if (scopeRef.current !== target) return;
      if (response.statusCode !== 1000) throw new Error(response.errors?.map((e: any) => typeof e === 'string' ? e : e.message).join(' ') || t('ai.actionFailed'));
      setManageOpen(false); setConfirmClear(false); setSuggestions(null);
      if (rebuild) setReload(v => v + 1);
      else { requestVersion.current++; setBusy(false); setThinking(false); setNotice(t('ai.quick.memoryCleared')); }
    } catch (err: any) {
      if (scopeRef.current === target) setError(err.message || t('ai.actionFailed'));
    } finally {
      if (scopeRef.current === target) setResetting(false);
    }
  };
  useEffect(() => {
    let active = true;
    setReady(false); setEntitled(null); setSetupError(''); setChecking(!!businessId && permitted);
    if (businessId && permitted) void apiService.getAiStatus().then(response => {
      if (!active) return;
      if (response.statusCode !== 1000 || !response.data) {
        setSetupError(response.errors?.map((e: any) => typeof e === 'string' ? e : e.message).join(' ') || t('ai.loadFailed'));
        return;
      }
      setEntitled(response.data.entitled); setReady(response.data.configured);
    }).catch((err: any) => { if (active) setSetupError(err.message || t('ai.loadFailed')); })
      .finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, [scope, permitted, setupReload]);

  useEffect(() => {
    const version = ++requestVersion.current;
    let active = true;
    const current = () => active && scopeRef.current === scope && requestVersion.current === version;
    setSuggestions(null); setError(''); setBusy(false); setThinking(false); setNotice('');
    if (!ready || !permitted || !contextReady || collapsed) return;
    setBusy(true);
    let poll: ReturnType<typeof setTimeout> | undefined;
    // The assistant agent may still be working: ask again every few seconds (about three minutes at most).
    const attempt = (round: number, refresh: boolean) => {
      void apiService.getAiQuickSuggestions(conversationId, locale, refresh).then(response => {
        if (!current()) return;
        if (response.statusCode !== 1000 || !response.data) throw new Error(response.errors?.map((e: any) => typeof e === 'string' ? e : e.message).join(' ') || t('ai.actionFailed'));
        if (response.data.kind === 'pending') {
          if (round >= 45) throw new Error(t('ai.quick.slow')); // about three minutes
          setThinking(true);
          poll = setTimeout(() => attempt(round + 1, false), 4000);
          return;
        }
        setThinking(false); setBusy(false);
        if (response.data.basedOnMessageId && response.data.basedOnMessageId === usedFor.current) { setShowQuick(true); return; }
        setShowQuick(false); setSuggestions(response.data);
      }).catch((err: any) => { if (current()) { setThinking(false); setBusy(false); setError(err.message || t('ai.actionFailed')); } });
    };
    // Opening a contact and loading messages settle together; generate only once.
    const timer = setTimeout(() => { const refresh = refreshNext.current; refreshNext.current = false; attempt(0, refresh); }, 700);
    return () => { active = false; clearTimeout(timer); if (poll) clearTimeout(poll); };
  }, [scope, contextVersion, contextReady, ready, permitted, collapsed, locale, reload]);

  // Text above the buttons: the agent's short line, else the shortest one-line reply, else a plain question.
  const choicesMessage = (s: AiQuickSuggestions) => s.choicesText
    || [...s.replies].filter(r => !r.includes('\n') && r.length <= 200).sort((a, b) => a.length - b.length)[0]
    || t('ai.quick.choosePrompt');

  useEffect(() => {
    if (!sentVersion) return;
    usedFor.current = suggestions?.basedOnMessageId || usedFor.current;
    setSuggestions(null); setShowQuick(true);
  }, [sentVersion]);
  useEffect(() => {
    if (!showQuick || quickSets) return;
    void apiService.getQuickReplySets({ isActive: true, limit: 20, skip: 0 })
      .then(response => setQuickSets(response.statusCode === 1000 ? response.data?.quickReplySets || [] : []))
      .catch(() => setQuickSets([]));
  }, [showQuick, quickSets]);
  const sendQuickSet = async (setId: string) => {
    setSendingSet(setId); setError('');
    try {
      const response = await apiService.sendQuickReplySet(setId, conversationId);
      if (response.statusCode !== 1000) throw new Error(response.errors?.[0]?.message || t('chat.quickReplySendFailed'));
      setNotice(t('chat.quickReplySent'));
    } catch (err: any) { setError(err.message || t('chat.quickReplySendFailed')); }
    finally { setSendingSet(null); }
  };

  // The agent's answers: one recommended message (built from the catalog) plus alternatives.
  const agentView = suggestions?.source === 'agent' && suggestions.kind === 'replies' && !!suggestions.replies.length;

  if (!businessId || !permitted || entitled === false) return null;
  const row = { flexDirection: isRTL ? 'row-reverse' as const : 'row' as const };
  const alignment = { textAlign: isRTL ? 'right' as const : 'left' as const };
  return <View style={styles.container}>
    <View style={[styles.header, row]}>
      <AiBotIcon size={18} color={colors.primary as string} />
      <Text style={styles.label} numberOfLines={1}>{suggestions?.agent ? t('ai.quick.asAgent', { name: suggestions.agent.name }) : t('ai.quick.title')}</Text>
      {busy && <ActivityIndicator size="small" color={colors.primary} />}
      <View style={styles.spacer} />
      <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('ai.quick.refresh')} disabled={busy || checking} style={styles.iconButton} onPress={() => { if (ready) { refreshNext.current = true; setReload(v => v + 1); } else setSetupReload(v => v + 1); }}><Ionicons name="refresh-outline" size={18} color={colors.primary} /></TouchableOpacity>
      {ready && <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('ai.quick.manage')} disabled={resetting} style={styles.iconButton} onPress={() => { setManageOpen(v => !v); setConfirmClear(false); }}><Ionicons name="ellipsis-horizontal" size={18} color={colors.text.secondary} /></TouchableOpacity>}
      <TouchableOpacity accessibilityRole="button" accessibilityLabel={t(collapsed ? 'ai.quick.expand' : 'ai.quick.collapse')} style={styles.iconButton} onPress={() => setCollapsed(v => !v)}><Ionicons name={collapsed ? 'chevron-down' : 'chevron-up'} size={18} color={colors.text.secondary} /></TouchableOpacity>
    </View>
    {manageOpen && <View style={[styles.actions, row]}>
      {!confirmClear ? <>
        <TouchableOpacity accessibilityRole="button" disabled={resetting} style={[styles.action, row]} onPress={() => void resetMemory(true)}>
          <Ionicons name="refresh-circle-outline" size={16} color={colors.primary} /><Text style={styles.use}>{t('ai.quick.rebuild')}</Text>
        </TouchableOpacity>
        <TouchableOpacity accessibilityRole="button" disabled={resetting} style={[styles.action, row]} onPress={() => setConfirmClear(true)}>
          <Ionicons name="trash-outline" size={16} color={colors.error} /><Text style={[styles.use, { color: colors.error }]}>{t('ai.quick.clearMemory')}</Text>
        </TouchableOpacity>
      </> : <>
        <Text style={[styles.hint, alignment]}>{t('ai.quick.clearConfirm')}</Text>
        <TouchableOpacity accessibilityRole="button" disabled={resetting} style={[styles.action, row]} onPress={() => void resetMemory(false)}>
          <Text style={[styles.use, { color: colors.error }]}>{t('ai.quick.clearMemory')}</Text>
        </TouchableOpacity>
        <TouchableOpacity accessibilityRole="button" disabled={resetting} style={[styles.action, row]} onPress={() => setConfirmClear(false)}>
          <Text style={styles.use}>{t('common.cancel')}</Text>
        </TouchableOpacity>
      </>}
      {resetting && <ActivityIndicator size="small" color={colors.primary} />}
    </View>}
    {!collapsed && <>
      {!!notice && <Text style={[styles.hint, alignment]}>{notice}</Text>}
      {!ready && <Text style={[setupError ? styles.error : styles.hint, alignment]}>{checking ? t('ai.assistant.checkingSetup') : setupError || t('ai.assistant.setupRequired')}</Text>}
      {busy && <Text style={[styles.hint, alignment]}>{t(thinking ? 'ai.quick.thinking' : 'ai.quick.preparing')}</Text>}
      {!!suggestions?.summary && <Text style={[styles.hint, alignment]} numberOfLines={2}>{t('ai.quick.summary')}: {suggestions.summary}</Text>}
      {!!error && <Text accessibilityRole="alert" style={[styles.error, alignment]}>{error}</Text>}
      {showQuick && !suggestions && !busy && <View style={styles.choicePreview}>
        <Text style={[styles.hint, alignment]}>{t('ai.quick.quickReplies')}</Text>
        {quickSets === null ? <ActivityIndicator size="small" color={colors.primary} />
          : quickSets.length ? <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.cards, row]}>
            {quickSets.map(set => <TouchableOpacity key={set._id} accessibilityRole="button" disabled={disabled || !!sendingSet} style={[styles.action, row, (disabled || !!sendingSet) && styles.disabled]} onPress={() => void sendQuickSet(set._id)}>
              {sendingSet === set._id ? <ActivityIndicator size="small" color={colors.primary} /> : <Ionicons name="flash-outline" size={16} color={colors.primary} />}
              <Text style={styles.use}>{set.name}</Text>
            </TouchableOpacity>)}
          </ScrollView> : <Text style={[styles.hint, alignment]}>{t('ai.quick.noQuickReplies')}</Text>}
      </View>}
      {agentView && suggestions && <>
        {suggestions.needsHuman && <Text accessibilityRole="alert" style={[styles.error, alignment]}>{t('ai.quick.needsHuman')}</Text>}
        <View style={styles.recommended}>
          <Text style={[styles.label, alignment]}>{t('ai.quick.recommended')}</Text>
          <Text style={[styles.replyText, alignment]}>{suggestions.replies[0]}</Text>
          {!!suggestions.choices?.length && <View style={[styles.actions, row]}>{suggestions.choices.map(choice => <View key={choice.id} style={styles.choiceChip}>
            <Text style={styles.label}>{choice.label}</Text>{!!choice.description && <Text style={styles.choiceDescription}>{choice.description}</Text>}
          </View>)}</View>}
          <View style={[styles.actions, row]}>
            {!!onSendNow && <TouchableOpacity accessibilityRole="button" disabled={disabled} style={[styles.sendButton, row, disabled && styles.disabled]} onPress={() => {
              onSendNow(suggestions.replies[0], suggestions.choices || []);
              // The recommendation's product cards / photos go right after its text.
              (suggestions.media || []).forEach(item => onSendMedia?.(item));
            }}>
              <Ionicons name="send" size={14} color={colors.white as string} /><Text style={styles.sendText}>{t('ai.quick.send')}</Text>
            </TouchableOpacity>}
            <TouchableOpacity accessibilityRole="button" disabled={disabled} style={[styles.action, row, disabled && styles.disabled]} onPress={() => { setCollapsed(true); if (suggestions.choices?.length) onUseChoices(suggestions.replies[0], suggestions.choices); else onUseReply(suggestions.replies[0]); }}>
              <Ionicons name="create-outline" size={16} color={colors.primary} /><Text style={styles.use}>{t('ai.quick.edit')}</Text>
            </TouchableOpacity>
          </View>
        </View>
        {!!suggestions.media?.length && !!onSendMedia && <View style={[styles.actions, row]}>
          {suggestions.media.map(item => <TouchableOpacity key={`${item.kind}:${item.id}`} accessibilityRole="button" disabled={disabled} style={[styles.action, row, disabled && styles.disabled]} onPress={() => onSendMedia(item)}>
            <Ionicons name={item.kind === 'images' ? 'images-outline' : item.kind === 'catalog' ? 'grid-outline' : 'pricetag-outline'} size={16} color={colors.primary} />
            <Text style={styles.use} numberOfLines={1}>{t(item.kind === 'images' ? 'ai.media.sendPhotos' : item.kind === 'catalog' ? 'ai.media.sendCatalog' : 'ai.media.sendProduct', { name: item.label || '' })}</Text>
          </TouchableOpacity>)}
        </View>}
        {!!suggestions.cart?.lines.length && <Text style={[styles.cartChip, alignment]}>🛒 {t('ai.quick.cartSummary', { count: suggestions.cart.lines.reduce((n, l) => n + l.quantity, 0), total: `${suggestions.cart.total} ${suggestions.cart.currency}` })}</Text>}
        {suggestions.replies.length > 1 && <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.cards, row]}>
          {suggestions.replies.slice(1).map(reply => <TouchableOpacity key={reply} accessibilityRole="button" disabled={disabled} style={[styles.replyCard, disabled && styles.disabled]} onPress={() => onUseReply(reply)}><Text style={[styles.replyText, alignment]}>{reply}</Text><Text style={[styles.use, alignment]}>{t('ai.quick.useReply')}</Text></TouchableOpacity>)}
        </ScrollView>}
      </>}
      {!agentView && !!suggestions?.replies.length && <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.cards, row]}>
        {suggestions.replies.map(reply => <TouchableOpacity key={reply} accessibilityRole="button" disabled={disabled} style={[styles.replyCard, disabled && styles.disabled]} onPress={() => onUseReply(reply)}><Text style={[styles.replyText, alignment]}>{reply}</Text><Text style={[styles.use, alignment]}>{t('ai.quick.useReply')}</Text></TouchableOpacity>)}
      </ScrollView>}
      {!agentView && !!suggestions?.choices?.length && <View style={styles.choicePreview}>
        <Text style={[styles.hint, alignment]}>{t('ai.quick.choicePreview')}</Text>
        <Text style={[styles.replyText, alignment]}>{choicesMessage(suggestions)}</Text>
        <View style={[styles.actions, row]}>{suggestions.choices.map(choice => <Text key={choice.id} style={styles.choiceLabel}>{choice.label}</Text>)}</View>
        <TouchableOpacity accessibilityRole="button" disabled={disabled || !suggestions.replies[0]} style={[styles.action, row]} onPress={() => { setCollapsed(true); onUseChoices(choicesMessage(suggestions), suggestions.choices!); }}><Ionicons name="options-outline" size={16} color={colors.primary} /><Text style={styles.use}>{t('ai.quick.addChoices')}</Text></TouchableOpacity>
      </View>}
      {!!suggestions?.issues?.length && <Text style={[styles.hint, alignment]}>{t('ai.quick.clarifyFirst')}</Text>}
      {suggestions?.kind === 'templates' && <>
        <Text style={[styles.hint, alignment]}>{t(suggestions.reason === 'window_closed' ? 'ai.quick.windowClosed' : 'ai.quick.firstContact')}</Text>
        {suggestions.templates.length ? <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.cards, row]}>
          {suggestions.templates.map(template => <TouchableOpacity key={template.id} accessibilityRole="button" disabled={disabled} style={[styles.replyCard, disabled && styles.disabled]} onPress={() => onSelectTemplate(template.id)}>
            <Text style={[styles.label, alignment]}>{template.name}</Text><Text numberOfLines={2} style={[styles.replyText, alignment]}>{template.body}</Text><Text style={[styles.use, alignment]}>{t('ai.quick.prepareTemplate')}</Text>
          </TouchableOpacity>)}
        </ScrollView> : <Text style={[styles.hint, alignment]}>{t(suggestions.reason === 'permission' ? 'ai.quick.templatePermission' : suggestions.reason === 'channel' ? 'ai.quick.noChannelTemplates' : 'ai.quick.noTemplates')}</Text>}
      </>}
      {!!suggestions?.actions.length && <View style={[styles.actions, row]}>
        {suggestions.actions.map(action => <TouchableOpacity key={action.type} accessibilityRole="button" disabled={disabled} style={[styles.action, row, disabled && styles.disabled]} onPress={() => onAction(action.type, action.lines)}>
          <Ionicons name={action.type === 'order' ? 'cart-outline' : 'document-text-outline'} size={16} color={colors.primary} /><Text style={styles.use}>{t(action.type === 'order' ? 'ai.quick.order' : 'ai.quick.quotation')}</Text>
        </TouchableOpacity>)}
      </View>}
    </>}
  </View>;
}

const getStyles = () => StyleSheet.create({
  container: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, gap: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border, backgroundColor: colors.background.surface },
  header: { alignItems: 'center', gap: spacing.sm },
  spacer: { flex: 1 },
  iconButton: { padding: spacing.xs },
  label: { fontSize: 12, fontWeight: '600', color: colors.text.primary },
  hint: { fontSize: 12, color: colors.text.secondary },
  error: { fontSize: 12, color: colors.error },
  cards: { gap: spacing.sm, paddingBottom: 2 },
  replyCard: { width: 220, gap: spacing.sm, padding: spacing.sm, borderWidth: 1, borderColor: colors.border, borderRadius: 10, backgroundColor: colors.background.primary },
  replyText: { fontSize: 13, lineHeight: 19, color: colors.text.primary },
  use: { fontSize: 12, fontWeight: '500', color: colors.primary },
  actions: { flexWrap: 'wrap', gap: spacing.sm },
  action: { alignItems: 'center', gap: spacing.xs, padding: spacing.sm, borderRadius: 8, borderWidth: 1, borderColor: colors.border },
  disabled: { opacity: 0.45 },
  choicePreview: { gap: spacing.sm },
  recommended: { gap: spacing.sm, padding: spacing.sm, borderWidth: 1, borderColor: colors.primary, borderRadius: 10, backgroundColor: colors.background.primary },
  choiceChip: { padding: spacing.xs, borderWidth: 1, borderColor: colors.border, borderRadius: 6, maxWidth: 220 },
  choiceDescription: { fontSize: 11, color: colors.text.secondary },
  sendButton: { alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, borderRadius: 8, backgroundColor: colors.primary },
  sendText: { fontSize: 13, fontWeight: '600', color: colors.white },
  cartChip: { fontSize: 13, fontWeight: '600', color: colors.text.primary },
  choiceLabel: { fontSize: 12, color: colors.text.primary, padding: spacing.xs, borderWidth: 1, borderColor: colors.border, borderRadius: 6 },
});
